import { ActivatedRoute, Router } from '@angular/router';
import {
  AgActionsMenuCellRendererComponent,
  AgActionsMenuItem,
} from 'src/app/ag-grid/components/cell-renderers/ag-actions-menu-cell-renderer/ag-actions-menu-cell-renderer.component';
import {
  ChangeDetectorRef,
  Component,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ViewId, ViewPresetService } from '@services/view-preset.service';

import { AgBooleanCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgButtonCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/button-cell-renderer/button-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgDateTimeFilterComponent } from '../../../ag-grid/components/ag-date-time-filter/ag-date-time-filter.component';
import { AppState } from 'src/app/reducers';
import { AutoStoryPayload } from '../../services/feed.service';
import { AutoStoryToolDialogComponent } from '../../../shared/components/auto-story-tool-dialog/auto-story-tool-dialog.component';
import { ColDef } from 'ag-grid-community';
import { ConfirmDialogComponent } from '../../components/popups/confirm-dialog/confirm-dialog.component';
import { CoreService } from '../../services/core.service';
import { GeneralDynamicAgGridComponent } from 'src/app/ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { Observable } from 'rxjs';
import { PostService } from '../../services/post.service';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from 'src/app/core/services/utils.service';
import {
  NomAlgorithmTaskStatus,
  NomObjectLifecycleStatus,
} from 'src/app/core/constants';
import { redirectToObject } from 'src/app/shared/utils/object-utils';
import { JsonViewerDialogComponent } from '../../components/popups/json-viewer-dialog/json-viewer-dialog.component';

import {
  faDownload,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';

import { toSignal } from '@angular/core/rxjs-interop';
import { selectUserAccessLevelGlobalAdmin } from 'src/app/core/reducers';

@Component({
  selector: 'app-auto-story-runs',
  templateUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.html',
  styleUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AutoStoryRunsComponent extends GeneralDynamicAgGridComponent {
  protected tableTitle = 'Auto Story Tool Runs';
  protected tableIcon: IconProp = ['fas', 'wand-magic-sparkles'];

  public viewId: ViewId = ViewId.AutoStoryRuns;

  hasMasterDetail = false;

  public components: {
    [p: string]: any;
  } = {
    agDateInput: AgDateTimeFilterComponent,
  };

  protected generalGridClassName: string = 'auto-story-runs-grid';

  protected dialogService = inject(DialogService);

  // ! stage_failure_output is only returned to global admins (access level 50+)
  private isGlobalAdmin = toSignal(
    inject(Store<AppState>).select(selectUserAccessLevelGlobalAdmin),
    { initialValue: false },
  );

  constructor(
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected router: Router,
    protected coreService: CoreService,
    protected postService: PostService,
    protected cdr: ChangeDetectorRef,
    protected logger: LoggerService,
    protected viewPresetService: ViewPresetService,
    protected toastService: ToastService,
    protected translateService: TranslateService,
    protected utilsService: UtilsService,
  ) {
    super(
      store,
      route,
      cdr,
      logger,
      router,
      viewPresetService,
      toastService,
      translateService,
    );

    this.tableTitle = this.translateService.instant(
      'auto-story-runs-grid.title.auto-story-runs',
    );
  }

  protected fetchAgGridData(request: any): Observable<any> {
    return this.coreService.fetchUserAutoStoryRunsAgGrid(request);
  }

  private deleteRun(data: any): void {
    const runId = data?.id;
    if (!runId) {
      this.toastService.showError(
        this.translateService.instant(
          'auto-story-runs.error.missing-object-id',
        ),
      );
      return;
    }

    const message = this.translateService.instant(
      'auto-story-runs.delete-popup.confirm-message',
      { id: runId ?? '' },
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason !== DialogCloseReason.Confirm) {
          return;
        }

        this.postService.deleteAutoStoryRun(runId).subscribe({
          next: (result) => {
            if (result?.success) {
              this.toastService.showSuccess(
                this.translateService.instant(
                  'auto-story-runs.success.run-deleted',
                ),
              );
              this.gridApi?.refreshServerSide({ purge: true });
            } else {
              this.toastService.showError(
                this.translateService.instant(
                  'auto-story-runs.error.delete-run-failed',
                ),
              );
            }
          },
          error: (err) => {
            this.logger.error('Failed to delete auto story run', err);
            this.toastService.showError(
              this.translateService.instant(
                'auto-story-runs.error.delete-run-failed',
              ),
            );
          },
        });
      },
    );
  }

  private runAsNew(data: any): void {
    const initialPayload = this.extractPayloadFromRun(data);

    this.dialogService.openSimpleDialog(AutoStoryToolDialogComponent, {
      data: { initialPayload },
      closeOnClick: false,
      closeOnEscape: true,
    });
  }

  private downloadResult(data: any): void {
    const url = data?.absolute_url;
    if (!url) return;

    window.open(url, '_blank', 'noopener');
  }

  private hasStageFailureOutput(data: any): boolean {
    const output = data?.stage_failure_output;
    if (!output) return false;

    return typeof output === 'object' ? Object.keys(output).length > 0 : true;
  }

  private openStageFailureOutputDialog(data: any): void {
    if (!this.hasStageFailureOutput(data)) return;

    this.dialogService.openSimpleDialog(JsonViewerDialogComponent, {
      data: {
        title: this.translateService.instant(
          'auto-story-runs.dialog.stage-failure-output',
        ),
        json: data.stage_failure_output,
      },
      closeOnClick: false,
      closeOnEscape: true,
      width: '40%',
      height: '70%',
      minWidth: 500,
      minHeight: 800,
      maxWidth: 1020,
      maxHeight: 1220,
    });
  }

  private extractPayloadFromRun(data: any): Partial<AutoStoryPayload> {
    const raw = data?.params ?? {};
    return {
      prompt_template_id: raw.prompt_template_id,
      prompt_params: raw.prompt_params,
      model: raw.model,
      clusters: raw.clusters,
      distance_threshold: raw.distance_threshold,
      sanitize_mentions: raw.sanitize_mentions,
      sanitize_urls: raw.sanitize_urls,
      sort: raw.sort,
      sample_metric: raw.sample_metric,
      sampling_percentile_groups: raw.sampling_percentile_groups,
      sampling_percentage_per_group: raw.sampling_percentage_per_group,
      sample_length: raw.sample_length,
    };
  }

  createColumnDefs() {
    const rowActions: AgActionsMenuItem[] = [
      {
        label: this.translateService.instant(
          'auto-story-runs.action.delete-run',
        ),
        icon: ['fas', 'trash'],
        action: (data) => this.deleteRun(data),
      },
      {
        label: this.translateService.instant(
          'auto-story-runs.action.run-as-new',
        ),
        icon: ['fas', 'rotate-right'],
        action: (data) => this.runAsNew(data),
      },
    ];

    const columnDefs: ColDef[] = [
      {
        headerName: this.translateService.instant('grid.column.id'),
        field: 'id',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 80,
        maxWidth: 80,
        pinned: 'left',
        cellRenderer: AgActionsMenuCellRendererComponent,
        cellRendererParams: {
          titleKey: 'id',
          items: rowActions,
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.company'),
        field: 'company.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 200,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'company.name',
          objectId: 'company.id',
          objectClassId: 'company.class_id',
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.object-class'),
        field: 'object.class_name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 120,
      },
      {
        headerName: this.translateService.instant('grid.column.object-type'),
        field: 'object.type_name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 140,
      },
      {
        headerName: this.translateService.instant('grid.column.object'),
        field: 'object.id',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 220,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'object.name',
          objectId: 'object.id',
          objectClassId: 'object.class_id',
          objectTypeId: 'object.type_id',
          onClick: (data) => {
            if (
              data?.object.lifecycle_status.id ===
                NomObjectLifecycleStatus.ACTIVE &&
              data.object.can_view
            ) {
              redirectToObject(data.object, this.router, true);
            } else {
              this.toastService.showInfo(
                this.translateService.instant(
                  'autostory-runs.error.you-do-not-have-permissions-or-object-not-active',
                ),
                this.translateService.instant(
                  'autostory-runs.error.not-available',
                ),
              );
            }
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.user'),
        field: 'user.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 200,
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'user.name',
          objectId: 'user.id',
          objectClassId: 'user.class_id',
          avatarKey: 'user.avatar_url',
          onClick: (data) => {
            this.router.navigate(['admin', 'home', data.user.id]);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.user-email'),
        field: 'user.email',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 220,
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.is-scheduled'),
        field: 'is_scheduled',
        sortable: true,
        minWidth: 110,
        maxWidth: 140,
        filterParams: {
          values: ['true', 'false'],
        },
        cellRenderer: AgBooleanCellRendererComponent,
        cellRendererParams: {
          isActive: 'data.is_scheduled',
        },
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedule-run-id',
        ),
        field: 'schedule_run_id',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.time-started'),
        field: 'time_created.absolute',
        filter: 'agDateColumnFilter',
        sortable: true,
        cellRenderer: AgDateCellRendererComponent,
        filterParams: this.filterParams,
        minWidth: 180,
      },
      {
        headerName: this.translateService.instant('grid.column.time-updated'),
        field: 'time_updated.absolute',
        filter: 'agDateColumnFilter',
        sortable: true,
        sort: 'desc',
        cellRenderer: AgDateCellRendererComponent,
        filterParams: this.filterParams,
        minWidth: 180,
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.time-elapsed'),
        field: 'time_elapsed',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 100,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant('grid.column.task-status'),
        field: 'task_status.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 100,
        cellStyle: (params) => {
          const style = { fontWeight: 'var(--font-weight-medium)' };

          switch (params?.data?.task_status?.id) {
            case NomAlgorithmTaskStatus.FAILED:
              return { ...style, color: 'var(--color-feedback-danger-fg)' };
            case NomAlgorithmTaskStatus.COMPLETED:
              return { ...style, color: 'var(--color-feedback-success-fg)' };
            default:
              return style;
          }
        },
      },
      {
        headerName: this.translateService.instant('grid.column.email-status'),
        field: 'email_status.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 120,
      },
      {
        headerName: this.translateService.instant('grid.column.download'),
        field: 'absolute_url',
        filter: false,
        sortable: false,
        minWidth: 90,
        maxWidth: 110,
        valueFormatter: () => '',
        cellRendererSelector: (params) => {
          if (!params?.data?.absolute_url) return undefined;

          return {
            component: AgButtonCellRendererComponent,
            params: {
              variant: 'secondary',
              leadingIcon: faDownload,
              iconOnly: true,
              ariaLabel: this.translateService.instant(
                'auto-story-runs.action.download-result',
              ),
              action: this.downloadResult.bind(this),
            },
          };
        },
      },
      {
        headerName: this.translateService.instant(
          'grid.column.stage-failure-output',
        ),
        field: 'stage_failure_output',
        filter: false,
        sortable: false,
        minWidth: 110,
        maxWidth: 130,
        hide: !this.isGlobalAdmin(),
        valueFormatter: () => '',
        cellRendererSelector: (params) => {
          // * no failure payload — the run cleared every stage
          if (!this.hasStageFailureOutput(params?.data)) {
            return {
              component: AgBooleanCellRendererComponent,
              params: { isActive: true },
            };
          }

          return {
            component: AgButtonCellRendererComponent,
            params: {
              variant: 'danger',
              leadingIcon: faExclamationTriangle,
              iconOnly: true,
              ariaLabel: this.translateService.instant(
                'auto-story-runs.dialog.stage-failure-output',
              ),
              action: this.openStageFailureOutputDialog.bind(this),
            },
          };
        },
      },
      {
        headerName: this.translateService.instant('grid.column.algorithm'),
        field: 'algorithm.name',
        filter: 'agTextColumnFilter',
        sortable: true,
        minWidth: 160,
        initialHide: true,
      },
      {
        ...this.createNomenclatureColumnDef({
          headerNameKey: 'grid.column.stage',
          fieldKey: 'stage.id',
          fieldValue: 'stage.name',
          nomenclatureName: 'algorithm_stage',
          otherFilterParams: {
            defaultToNothingSelected: true,
          },
        }),
        sortable: true,
        minWidth: 140,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.progress-percent',
        ),
        field: 'progress_percent',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 100,
        type: 'rightAligned',
      },
      {
        headerName: this.translateService.instant(
          'grid.column.data-set-items-count',
        ),
        field: 'dataset_items_count',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant('grid.column.data-set-size'),
        field: 'dataset_size_bytes',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 140,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        ...this.createNomenclatureColumnDef({
          headerNameKey: 'grid.column.llm-vendor',
          fieldKey: 'llm_vendor.id',
          fieldValue: 'llm_vendor.name',
          nomenclatureName: 'llm_vendor',
          otherFilterParams: {
            defaultToNothingSelected: true,
          },
        }),
        sortable: true,
        minWidth: 110,
      },
      {
        ...this.createNomenclatureColumnDef({
          headerNameKey: 'grid.column.llm-model',
          fieldKey: 'llm_model.id',
          fieldValue: 'llm_model.name',
          nomenclatureName: 'llm_model',
          otherFilterParams: {
            defaultToNothingSelected: true,
          },
        }),
        sortable: true,
        minWidth: 110,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-llm-batches',
        ),
        field: 'total_llm_batches',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 110,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-llm-failures',
        ),
        field: 'total_llm_failures',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 110,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-prompt-tokens',
        ),
        field: 'total_prompt_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-payload-tokens',
        ),
        field: 'total_payload_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 170,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-input-tokens',
        ),
        field: 'total_input_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 150,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-output-tokens',
        ),
        field: 'total_output_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        initialHide: true,
      },
      {
        headerName: this.translateService.instant('grid.column.total-tokens'),
        field: 'total_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant(
          'grid.column.total-cached-tokens',
        ),
        field: 'total_cached_tokens',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 160,
        type: 'rightAligned',
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
      },
      {
        headerName: this.translateService.instant('grid.column.total-cost'),
        field: 'total_cost',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 130,
        type: 'rightAligned',
        valueFormatter: (params) =>
          params.value != null ? '$' + Number(params.value).toFixed(4) : '',
      },
      {
        headerName: this.translateService.instant('grid.column.failed-stage'),
        field: 'failed_stage.id',
        filter: 'agNumberColumnFilter',
        sortable: true,
        minWidth: 140,
        type: 'rightAligned',
        initialHide: true,
        valueFormatter: (params) =>
          this.utilsService.formatNumberWithSpaces(params.value),
        //initialHide: true,
      },
      // {
      //   headerName: this.translateService.instant('grid.column.error-code'),
      //   field: 'error_code',
      //   filter: 'agTextColumnFilter',
      //   minWidth: 140,
      //   initialHide: true,
      // },
      // {
      //   headerName: this.translateService.instant('grid.column.error-message'),
      //   field: 'error_message',
      //   filter: 'agTextColumnFilter',
      //   minWidth: 240,
      //   initialHide: true,
      // },
      // {
      //   headerName: this.translateService.instant('grid.column.stack-trace'),
      //   field: 'stack_trace',
      //   filter: 'agTextColumnFilter',
      //   minWidth: 240,
      //   initialHide: true,
      // },
    ];

    return columnDefs;
  }
}
